/** Inventory / stock ledger endpoints (`/inventory`, internal staff only). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getData, getList, postData } from "../lib/apiClient";
import { queryKeys } from "./queryKeys";
import {
  LowStockItem,
  ProductType,
  StockDirection,
  StockMovement,
  StockReportItem,
  StockValuation,
} from "../types/models";

/** Movement types staff may record manually (production/sales are automatic). */
export type ManualMovementType =
  | "OPENING"
  | "PURCHASE_IN"
  | "RETURN"
  | "ADJUSTMENT";

export interface StockReportParams {
  page?: number;
  limit?: number;
  search?: string;
  categoryId?: string;
  type?: ProductType;
  lowStockOnly?: boolean;
}

export interface ListMovementsParams {
  page?: number;
  limit?: number;
  productId?: string;
  type?: StockMovement["type"];
  direction?: StockDirection;
  dateFrom?: string;
  dateTo?: string;
}

export interface CreateMovementInput {
  productId: string;
  type: ManualMovementType;
  quantity: number;
  /** Required only for `ADJUSTMENT` (IN/OUT). */
  direction?: StockDirection;
  unitCost?: number;
  note?: string;
}

export const inventoryApi = {
  stockReport: (params?: StockReportParams) =>
    getList<StockReportItem>("/inventory/stock", params),
  stockValuation: () => getData<StockValuation>("/inventory/stock/valuation"),
  lowStock: () => getData<LowStockItem[]>("/inventory/low-stock"),
  listMovements: (params?: ListMovementsParams) =>
    getList<StockMovement>("/inventory/movements", params),
  createMovement: (input: CreateMovementInput) =>
    postData<{ movementId: string }>("/inventory/movements", input),
  productLedger: (id: string, params?: { page?: number; limit?: number }) =>
    getList<StockMovement>(`/inventory/products/${id}/ledger`, params),
};

// ── Hooks ────────────────────────────────────────────────────────────

export const useStockReport = (params?: StockReportParams) =>
  useQuery({
    queryKey: queryKeys.inventory.stock(params),
    queryFn: () => inventoryApi.stockReport(params),
    placeholderData: (prev) => prev,
  });

export const useStockValuation = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.inventory.valuation,
    queryFn: () => inventoryApi.stockValuation(),
    enabled,
  });

export const useLowStock = () =>
  useQuery({
    queryKey: queryKeys.inventory.lowStock,
    queryFn: () => inventoryApi.lowStock(),
  });

export const useStockMovements = (params?: ListMovementsParams) =>
  useQuery({
    queryKey: queryKeys.inventory.movements(params),
    queryFn: () => inventoryApi.listMovements(params),
    placeholderData: (prev) => prev,
  });

export const useProductLedger = (
  id: string | undefined,
  params?: { page?: number; limit?: number },
) =>
  useQuery({
    queryKey: queryKeys.inventory.ledger(id ?? "", params),
    queryFn: () => inventoryApi.productLedger(id as string, params),
    enabled: Boolean(id),
  });

export const useCreateMovement = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMovementInput) =>
      inventoryApi.createMovement(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.inventory.all });
      qc.invalidateQueries({ queryKey: queryKeys.products.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.overview });
    },
  });
};
